import { useMemo } from 'react'

import { Lookup, useLookup } from '@/providers'
import IconWithPopover from '@components/IconWithPopover/IconWithPopover'
import { RHFCheckbox, RHFTextField } from '@components/RHFInputs'
import { InputAdornment, MenuItem, Stack, Typography } from '@mui/material'
import { getCurrencySymbol } from '@utils/stringHelpers'
import { UseFormReturn, useWatch } from 'react-hook-form'

import { chooseColor, useBasketCurrency } from './BasketSettings.utils'
import Program from './Program'

type Props = {
	mode: 'NEW' | 'EDIT'
	parentForm?: UseFormReturn<any>
}

/**
 * Primary basket matching settings fields (tier, groups, distributor, annualization,
 * direct deals, high-volume manufacturers, FOB, and programs).
 */
export default function MainSettings({ mode, parentForm = null }: Props) {
	const purchaseGroups = useLookup(Lookup.PurchaseGroups)
	const currency = useBasketCurrency(parentForm)
	const currencySymbol = useMemo(() => getCurrencySymbol(currency), [currency])

	const [switchDirectDeals, estimatedFob, highVolumeMfr, tier, ignoreCustomersWithInvalidTiers] =
		useWatch({
			name: [
				'switchDirectDeals',
				'estimatedFob.enabled',
				'highVolumeMfr',
				'tier',
				'ignoreCustomersWithInvalidTiers',
			],
		})

	return (
		<>
			{mode === 'EDIT' && (
				<Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
					<RHFTextField
						name="tier"
						select
						size="small"
						label="Tier"
						sx={{ width: '25rem' }}
						data-testid="settings-tier-select"
					>
						<MenuItem key="Tier 1" value="Tier 1" data-testid="settings-tier-select-tier1">
							Tier 1
						</MenuItem>
						<MenuItem key="Tier 2" value="Tier 2" data-testid="settings-tier-select-tier2">
							Tier 2
						</MenuItem>
						<MenuItem
							key="Rebate Only"
							value="Rebate Only"
							data-testid="settings-tier-select-rebate-only"
						>
							Rebate Only
						</MenuItem>
						<MenuItem
							key="Customer Tier"
							value="Customer Tier"
							data-testid="settings-tier-select-customer-tier"
						>
							Customer Tier
						</MenuItem>
					</RHFTextField>

					<Stack
						spacing={1}
						direction="row"
						justifyContent="flex-end"
						sx={{ alignItems: 'center' }}
					>
						<RHFCheckbox
							disabled={tier !== 'Customer Tier'}
							name="ignoreCustomersWithInvalidTiers"
							data-testid="settings-ignore-cust-invalid-tier"
						/>
						<Typography variant="body2" color={chooseColor(ignoreCustomersWithInvalidTiers)}>
							Ignore customer if Tier is invalid
						</Typography>
					</Stack>
				</Stack>
			)}

			{/* Purchase Groups */}
			<RHFTextField name="groupBy" select size="small" label="Group Products by" fullWidth>
				{purchaseGroups.map((group) => (
					<MenuItem
						key={group.id}
						value={group.id}
						data-testid={`settings-group-by-${group.label}`}
					>
						{group.label}
					</MenuItem>
				))}
			</RHFTextField>

			{/* Annualization */}
			<Stack spacing={2} direction="row" sx={{ alignItems: 'center' }} pl={1}>
				<Typography variant="body1">Annualization Period</Typography>
				<RHFTextField
					name="annualizationMonths"
					type="number"
					size="small"
					sx={{ width: '8rem' }}
					slotProps={{
						htmlInput: {
							min: 0.5,
							max: 12,
							step: 0.5,
							'data-testid': 'settings-annualization-months',
						},
						input: {
							endAdornment: <InputAdornment position="end">mon</InputAdornment>,
						},
					}}
				/>
				<IconWithPopover>
					Define the number of month&apos;s used to
					<br />
					determine the annual period for the customer.
				</IconWithPopover>
			</Stack>

			{/* Direct Deals */}
			<Stack spacing={2} direction="row" sx={{ alignItems: 'center' }}>
				<RHFCheckbox name="switchDirectDeals" data-testid="settings-switch-direct-deals" />
				<Typography variant="body2" color={chooseColor(switchDirectDeals)}>
					Recommend switching Direct Deals to BEPL
				</Typography>
				<IconWithPopover>
					When checked, purchases with direct deals
					<br />
					will be eligible for product matching
				</IconWithPopover>
			</Stack>

			{/* Highlight High Volume Manufacturers */}
			<Stack spacing={2} direction="row" sx={{ alignItems: 'center', mt: 1 }}>
				<RHFCheckbox name="highVolumeMfr.enabled" data-testid="settings-high-volume-mfr-enabled" />
				<Typography variant="body2" color={chooseColor(highVolumeMfr?.enabled)}>
					Highlight high purchase volume
				</Typography>

				{/* Annual thresholds */}
				<RHFTextField
					name="highVolumeMfr.cases"
					label="Annual Cases ≥"
					disabled={!highVolumeMfr?.enabled}
					type="number"
					size="small"
					sx={{ width: '8rem' }}
					slotProps={{
						input: {
							inputProps: { min: 0, step: 1, 'data-testid': 'settings-high-volume-mfr-cases' },
						},
					}}
				/>

				<RHFTextField
					name="highVolumeMfr.spend"
					label="Annual Spend ≥"
					disabled={!highVolumeMfr?.enabled}
					type="number"
					size="small"
					sx={{ width: '8rem' }}
					slotProps={{
						input: {
							startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
							inputProps: { min: 0, 'data-testid': 'settings-high-volume-mfr-spend' },
						},
					}}
				/>

				<IconWithPopover>
					If the manufacturer on matched products exceeds
					<br />
					the threshold amounts annually,the matched <br />
					products will require review.
					<br /> (🚩 High Volume MFR).
				</IconWithPopover>
			</Stack>

			{/* FOB */}
			<Stack spacing={2} direction="row" sx={{ alignItems: 'center' }}>
				<RHFCheckbox name="estimatedFob.enabled" data-testid="settings-estimated-fob-enabled" />
				<Typography variant="body2" color={chooseColor(estimatedFob)}>
					Add estimated FOB charge
				</Typography>
				<RHFTextField
					name="estimatedFob.value"
					disabled={!estimatedFob}
					type="number"
					size="small"
					sx={{ width: '6rem' }}
					slotProps={{
						input: {
							endAdornment: <InputAdornment position="end">%</InputAdornment>,
							inputProps: { 'data-testid': 'settings-estimated-fob-value' },
						},
					}}
				/>
				<IconWithPopover>
					The estimated FOB will only be added to matches
					<br />
					whose &quot;Price value type&quot; is &quot;Unit cost FOB&quot;
					<br />
				</IconWithPopover>
			</Stack>

			<Program />
		</>
	)
}
